import type { EnvironmentSeason } from "../render/environment.ts";

export interface SeasonalSurfacePalette {
  readonly terrain: number;
  readonly terrainUsesVertexColors: boolean;
  readonly landCover: number;
  readonly oldTownCarriageway: number;
  readonly oldTownJunction: number;
  readonly oldTownSidewalk: number;
  readonly oldTownCurb: number;
  readonly oldTownPedestrian: number;
  readonly generalCarriageway: number;
  readonly generalJunction: number;
  readonly generalSidewalk: number;
  readonly generalCurb: number;
  readonly generalPedestrian: number;
  readonly oldTownDistantCarriageway: number;
  readonly oldTownDistantPedestrian: number;
  readonly generalDistantCarriageway: number;
  readonly generalDistantPedestrian: number;
  readonly waterDeep: number;
  readonly waterShallow: number;
  readonly waterSky: number;
  readonly waterRippleScale: number;
  readonly treeTrunk: number;
  readonly treeBranch: number;
  readonly treeBirchTrunk: number;
  readonly treeEvergreenTrunk: number;
  readonly treeCrownDark: number;
  readonly treeCrownLight: number;
  readonly treeCrownOpacity: number;
  readonly treeEvergreenDark: number;
  readonly treeEvergreenLight: number;
  readonly treeBlossom: number;
  readonly treeBlossomOpacity: number;
  readonly treeSnow: number;
  readonly treeSnowOpacity: number;
  readonly facadeTint: number;
  readonly facadeTintStrength: number;
  readonly trimTint: number;
  readonly trimTintStrength: number;
  readonly roofTint: number;
  readonly roofTintStrength: number;
}

/**
 * Seasonal art direction is centralized so the dev menu and streamed world
 * always make the same, reversible material decision.
 */
export const SEASONAL_SURFACE_PALETTES: Readonly<
  Record<EnvironmentSeason, SeasonalSurfacePalette>
> = Object.freeze({
  spring: {
    terrain: 0xf3f6e8,
    terrainUsesVertexColors: true,
    landCover: 0x718263,
    oldTownCarriageway: 0x898d88,
    oldTownJunction: 0x90938e,
    oldTownSidewalk: 0xb4b4aa,
    oldTownCurb: 0x9b9b92,
    oldTownPedestrian: 0xaaaaa1,
    generalCarriageway: 0x818886,
    generalJunction: 0x858c89,
    generalSidewalk: 0xb9b5aa,
    generalCurb: 0xb0aa9d,
    generalPedestrian: 0xaca89d,
    oldTownDistantCarriageway: 0x646b65,
    oldTownDistantPedestrian: 0x898a7e,
    generalDistantCarriageway: 0x6a716b,
    generalDistantPedestrian: 0x8e8c82,
    waterDeep: 0x315d63,
    waterShallow: 0x769591,
    waterSky: 0xb2c0b6,
    waterRippleScale: 1.08,
    treeTrunk: 0x4b3b2f,
    treeBranch: 0x554235,
    treeBirchTrunk: 0xc7c5b9,
    treeEvergreenTrunk: 0x73523b,
    treeCrownDark: 0x456849,
    treeCrownLight: 0x77945f,
    treeCrownOpacity: 1,
    treeEvergreenDark: 0x31543e,
    treeEvergreenLight: 0x4a704f,
    treeBlossom: 0xe7c3bb,
    treeBlossomOpacity: 0.86,
    treeSnow: 0xe5e8e5,
    treeSnowOpacity: 0,
    facadeTint: 0xf0e7cf,
    facadeTintStrength: 0.06,
    trimTint: 0xf6f1df,
    trimTintStrength: 0.08,
    roofTint: 0xa66d50,
    roofTintStrength: 0.08,
  },
  summer: {
    terrain: 0xffffff,
    terrainUsesVertexColors: true,
    landCover: 0x606b56,
    oldTownCarriageway: 0x8c8d88,
    oldTownJunction: 0x92938e,
    oldTownSidewalk: 0xb0aea6,
    oldTownCurb: 0x96948c,
    oldTownPedestrian: 0xa6a39b,
    generalCarriageway: 0x858886,
    generalJunction: 0x898c89,
    generalSidewalk: 0xb7b0a4,
    generalCurb: 0xafa697,
    generalPedestrian: 0xaaa397,
    oldTownDistantCarriageway: 0x666963,
    oldTownDistantPedestrian: 0x85847a,
    generalDistantCarriageway: 0x6d716d,
    generalDistantPedestrian: 0x8c8980,
    waterDeep: 0x294f54,
    waterShallow: 0x657f78,
    waterSky: 0xaab8ae,
    waterRippleScale: 1,
    treeTrunk: 0x4b3b2f,
    treeBranch: 0x4b3b2f,
    treeBirchTrunk: 0xc2c0b5,
    treeEvergreenTrunk: 0x6f4d35,
    treeCrownDark: 0x38533e,
    treeCrownLight: 0x506a49,
    treeCrownOpacity: 1,
    treeEvergreenDark: 0x2d4a39,
    treeEvergreenLight: 0x426348,
    treeBlossom: 0xe7c3bb,
    treeBlossomOpacity: 0,
    treeSnow: 0xe5e8e5,
    treeSnowOpacity: 0,
    facadeTint: 0xffffff,
    facadeTintStrength: 0,
    trimTint: 0xffffff,
    trimTintStrength: 0,
    roofTint: 0xffffff,
    roofTintStrength: 0,
  },
  autumn: {
    terrain: 0xe5d5ab,
    terrainUsesVertexColors: true,
    landCover: 0x776943,
    oldTownCarriageway: 0x777b79,
    oldTownJunction: 0x7d807c,
    oldTownSidewalk: 0xa9a69d,
    oldTownCurb: 0x908e86,
    oldTownPedestrian: 0x9e9b92,
    generalCarriageway: 0x737a79,
    generalJunction: 0x777e7b,
    generalSidewalk: 0xaaa59a,
    generalCurb: 0xa29b8e,
    generalPedestrian: 0x9e988e,
    oldTownDistantCarriageway: 0x5b5f5b,
    oldTownDistantPedestrian: 0x7e7a70,
    generalDistantCarriageway: 0x626662,
    generalDistantPedestrian: 0x858178,
    waterDeep: 0x334b49,
    waterShallow: 0x6a766a,
    waterSky: 0x9b9c8e,
    waterRippleScale: 0.82,
    treeTrunk: 0x49372c,
    treeBranch: 0x49372c,
    treeBirchTrunk: 0xbdb7aa,
    treeEvergreenTrunk: 0x694731,
    treeCrownDark: 0x78492d,
    treeCrownLight: 0xa97435,
    treeCrownOpacity: 0.96,
    treeEvergreenDark: 0x30483a,
    treeEvergreenLight: 0x455d45,
    treeBlossom: 0xe7c3bb,
    treeBlossomOpacity: 0,
    treeSnow: 0xe5e8e5,
    treeSnowOpacity: 0,
    facadeTint: 0xb78152,
    facadeTintStrength: 0.1,
    trimTint: 0xd6bb91,
    trimTintStrength: 0.08,
    roofTint: 0x704b3e,
    roofTintStrength: 0.14,
  },
  winter: {
    terrain: 0xe2e7e5,
    terrainUsesVertexColors: false,
    landCover: 0xd8ddda,
    oldTownCarriageway: 0x737a7b,
    oldTownJunction: 0x7d8382,
    oldTownSidewalk: 0xc7cac6,
    oldTownCurb: 0xb8bcb9,
    oldTownPedestrian: 0xc0c4c0,
    generalCarriageway: 0x687174,
    generalJunction: 0x70797a,
    generalSidewalk: 0xc5c9c5,
    generalCurb: 0xb8bdb9,
    generalPedestrian: 0xbdc2be,
    oldTownDistantCarriageway: 0x666e6f,
    oldTownDistantPedestrian: 0xadb3af,
    generalDistantCarriageway: 0x6b7475,
    generalDistantPedestrian: 0xb5bbb7,
    waterDeep: 0x2c4248,
    waterShallow: 0x607277,
    waterSky: 0xa7b1ad,
    waterRippleScale: 0.38,
    treeTrunk: 0x4a4039,
    treeBranch: 0x50453d,
    treeBirchTrunk: 0xcbd0cc,
    treeEvergreenTrunk: 0x665044,
    treeCrownDark: 0xaeb8b4,
    treeCrownLight: 0xc6cdca,
    treeCrownOpacity: 0.15,
    treeEvergreenDark: 0x344b40,
    treeEvergreenLight: 0x50665a,
    treeBlossom: 0xe7c3bb,
    treeBlossomOpacity: 0,
    treeSnow: 0xe8ece9,
    treeSnowOpacity: 0.96,
    facadeTint: 0xb9c8ca,
    facadeTintStrength: 0.12,
    trimTint: 0xe4e9e7,
    trimTintStrength: 0.22,
    roofTint: 0xe3e7e4,
    roofTintStrength: 0.72,
  },
});

export interface AuthoredMaterialSeasonTreatment {
  readonly tint: number;
  readonly strength: number;
}

export function authoredMaterialSeasonTreatment(
  materialName: string,
  season: EnvironmentSeason,
): AuthoredMaterialSeasonTreatment {
  const profile = SEASONAL_SURFACE_PALETTES[season];
  const name = materialName.toLowerCase();
  if (/(leaf|foliage|crown|tree|shrub|hedge|grass)/.test(name)) {
    return {
      tint: profile.treeCrownLight,
      strength: season === "winter" ? 0.82 : season === "summer" ? 0 : 0.46,
    };
  }
  if (/(roof|terracotta|tile|slate|shingle)/.test(name)) {
    return {
      tint: profile.roofTint,
      strength: profile.roofTintStrength,
    };
  }
  if (/(ground|pav|cobble|stone|apron|runway|taxiway)/.test(name)) {
    return {
      tint: season === "winter"
        ? profile.oldTownSidewalk
        : profile.facadeTint,
      strength: season === "winter"
        ? 0.32
        : profile.facadeTintStrength * 0.45,
    };
  }
  return {
    tint: profile.facadeTint,
    strength: profile.facadeTintStrength * 0.55,
  };
}
